import { supabase, getSchema } from '@/lib/supabase';
import {
  mapDocumentFromDb,
  mapDocumentViewFromDb,
  mapUnassignedReceiptFromDb,
  type Document,
  type DocumentView,
  type UnassignedReceiptView,
  type UploadReceiptParams,
} from '../types';

/**
 * Get the storage bucket name based on environment
 */
function getDocumentBucketName(): string {
  const schema = getSchema();
  return schema === 'testing' ? 'project-documents-testing' : 'project-documents';
}

/**
 * Build the storage path for a document
 * Format: {business_id}/{project_id|unassigned}/{document_id}.{ext}
 */
function buildStoragePath(
  businessId: string,
  documentId: string,
  fileName: string,
  projectId?: string | null
): string {
  const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
  const folder = projectId || 'unassigned';
  return `${businessId}/${folder}/${documentId}.${extension}`;
}

/**
 * Upload a receipt to storage and create database record
 */
export async function uploadReceipt(params: UploadReceiptParams): Promise<Document> {
  const {
    file,
    businessId,
    uploadedBy,
    projectId = null,
    supplierId = null,
    documentType = 'receipt',
    source = 'web',
  } = params;

  const bucketName = getDocumentBucketName();
  const documentId = crypto.randomUUID();
  const filePath = buildStoragePath(businessId, documentId, file.name, projectId);

  // 1. Upload file to storage
  const { error: uploadError } = await supabase.storage
    .from(bucketName)
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    console.error('Error uploading receipt file:', uploadError);
    throw uploadError;
  }

  // 2. Create database record
  const documentRecord = {
    id: documentId,
    business_id: businessId,
    project_id: projectId,
    supplier_id: supplierId,
    uploaded_by: uploadedBy,
    uploaded_at: new Date().toISOString(),
    file_path: filePath,
    mime_type: file.type || null,
    document_type: documentType,
    source: source,
    ocr_status: 'not_started',
  };

  const { data, error: insertError } = await supabase
    .from('documents')
    .insert(documentRecord)
    .select()
    .single();

  if (insertError) {
    // Try to clean up uploaded file
    await supabase.storage.from(bucketName).remove([filePath]);
    console.error('Error creating document record:', insertError);
    throw insertError;
  }

  return mapDocumentFromDb(data);
}

/**
 * Get receipts for a specific project
 */
export async function getProjectReceipts(projectId: string): Promise<DocumentView[]> {
  const { data, error } = await supabase
    .from('v_documents')
    .select('*')
    .eq('project_id', projectId)
    .in('document_type', ['receipt', 'expense_invoice'])
    .order('uploaded_at', { ascending: false });

  if (error) {
    console.error('Error fetching project receipts:', error);
    throw error;
  }

  return (data || []).map(mapDocumentViewFromDb);
}

/**
 * Get all receipts for a business
 */
export async function getBusinessReceipts(businessId: string): Promise<DocumentView[]> {
  const { data, error } = await supabase
    .from('v_documents')
    .select('*')
    .eq('business_id', businessId)
    .in('document_type', ['receipt', 'expense_invoice'])
    .order('uploaded_at', { ascending: false });

  if (error) {
    console.error('Error fetching business receipts:', error);
    throw error;
  }

  return (data || []).map(mapDocumentViewFromDb);
}

/**
 * Get unassigned receipts for a business
 */
export async function getUnassignedReceipts(businessId: string): Promise<UnassignedReceiptView[]> {
  const { data, error } = await supabase
    .from('v_unassigned_receipts')
    .select('*')
    .eq('business_id', businessId)
    .order('uploaded_at', { ascending: false });

  if (error) {
    console.error('Error fetching unassigned receipts:', error);
    throw error;
  }

  return (data || []).map(mapUnassignedReceiptFromDb);
}

/**
 * Assign a receipt to a project (moves file in storage)
 */
export async function assignReceiptToProject(
  documentId: string,
  projectId: string,
  businessId: string
): Promise<Document> {
  const bucketName = getDocumentBucketName();

  // Get current document record
  const { data: currentDoc, error: fetchError } = await supabase
    .from('documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (fetchError || !currentDoc) {
    throw fetchError || new Error('Document not found');
  }

  const oldPath = currentDoc.file_path;
  const extension = oldPath.split('.').pop();
  const newPath = `${businessId}/${projectId}/${documentId}.${extension}`;

  // Move file in storage if path is changing
  if (oldPath !== newPath) {
    const { error: moveError } = await supabase.storage
      .from(bucketName)
      .move(oldPath, newPath);

    if (moveError) {
      console.error('Error moving receipt file:', moveError);
      throw moveError;
    }
  }

  // Update database record
  const { data, error: updateError } = await supabase
    .from('documents')
    .update({
      project_id: projectId,
      file_path: newPath,
    })
    .eq('id', documentId)
    .select()
    .single();

  if (updateError) {
    // Try to move file back
    if (oldPath !== newPath) {
      await supabase.storage.from(bucketName).move(newPath, oldPath);
    }
    throw updateError;
  }

  return mapDocumentFromDb(data);
}

/**
 * Assign a receipt to a supplier (no file move needed)
 */
export async function assignReceiptToSupplier(
  documentId: string,
  supplierId: string
): Promise<Document> {
  const { data, error } = await supabase
    .from('documents')
    .update({ supplier_id: supplierId })
    .eq('id', documentId)
    .select()
    .single();

  if (error) {
    console.error('Error assigning receipt to supplier:', error);
    throw error;
  }

  return mapDocumentFromDb(data);
}

/**
 * Update both project and supplier assignment
 */
export async function updateReceiptAssignments(
  documentId: string,
  businessId: string,
  projectId: string | null,
  supplierId: string | null
): Promise<Document> {
  const bucketName = getDocumentBucketName();

  // Get current document record
  const { data: currentDoc, error: fetchError } = await supabase
    .from('documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (fetchError || !currentDoc) {
    throw fetchError || new Error('Document not found');
  }

  const oldPath = currentDoc.file_path;
  const extension = oldPath.split('.').pop();
  const folder = projectId || 'unassigned';
  const newPath = `${businessId}/${folder}/${documentId}.${extension}`;

  // Move file in storage if path is changing
  if (oldPath !== newPath) {
    const { error: moveError } = await supabase.storage
      .from(bucketName)
      .move(oldPath, newPath);

    if (moveError) {
      console.error('Error moving receipt file:', moveError);
      throw moveError;
    }
  }

  // Update database record
  const { data, error: updateError } = await supabase
    .from('documents')
    .update({
      project_id: projectId,
      supplier_id: supplierId,
      file_path: newPath,
    })
    .eq('id', documentId)
    .select()
    .single();

  if (updateError) {
    // Try to move file back
    if (oldPath !== newPath) {
      await supabase.storage.from(bucketName).move(newPath, oldPath);
    }
    throw updateError;
  }

  return mapDocumentFromDb(data);
}

/**
 * Mark a receipt as unassigned (remove from project, keep supplier)
 */
export async function markReceiptUnassigned(
  documentId: string,
  businessId: string
): Promise<Document> {
  const bucketName = getDocumentBucketName();

  // Get current document record
  const { data: currentDoc, error: fetchError } = await supabase
    .from('documents')
    .select('*')
    .eq('id', documentId)
    .single();

  if (fetchError || !currentDoc) {
    throw fetchError || new Error('Document not found');
  }

  const oldPath = currentDoc.file_path;
  const extension = oldPath.split('.').pop();
  const newPath = `${businessId}/unassigned/${documentId}.${extension}`;

  // Move file in storage if path is changing
  if (oldPath !== newPath) {
    const { error: moveError } = await supabase.storage
      .from(bucketName)
      .move(oldPath, newPath);

    if (moveError) {
      console.error('Error moving receipt file:', moveError);
      throw moveError;
    }
  }

  // Update database record
  const { data, error: updateError } = await supabase
    .from('documents')
    .update({
      project_id: null,
      file_path: newPath,
    })
    .eq('id', documentId)
    .select()
    .single();

  if (updateError) {
    // Try to move file back
    if (oldPath !== newPath) {
      await supabase.storage.from(bucketName).move(newPath, oldPath);
    }
    throw updateError;
  }

  return mapDocumentFromDb(data);
}

/**
 * Get a signed URL for viewing a receipt
 */
export async function getReceiptUrl(filePath: string, expiresIn: number = 3600): Promise<string> {
  const bucketName = getDocumentBucketName();

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Error creating signed URL:', error);
    throw error;
  }

  return data.signedUrl;
}

/**
 * Delete a receipt (removes from storage and database)
 */
export async function deleteReceipt(documentId: string, filePath: string): Promise<void> {
  const bucketName = getDocumentBucketName();

  // Delete from storage
  const { error: storageError } = await supabase.storage
    .from(bucketName)
    .remove([filePath]);

  if (storageError) {
    console.error('Error deleting receipt from storage:', storageError);
    // Continue to delete from database anyway
  }

  // Delete from database
  const { error: dbError } = await supabase
    .from('documents')
    .delete()
    .eq('id', documentId);

  if (dbError) {
    console.error('Error deleting receipt from database:', dbError);
    throw dbError;
  }
}